import {
  Block,
  CreateForm,
  DialpadIcon,
  FormCol,
  FormRow,
  Link,
  LinkCollapse,
  TextControl,
  PrimarySubmitButton,
  ResourceAction,
  Spacing,
  moveWorkflowForwardByName,
  openModalByName,
  requestResourceByName,
  resetFormByName,
  useScopedSelector,
  InternalLink,
} from 'ia-react-core';
import { FormPageTitle, FormSection, InformationsSection } from 'omni-package-react-core';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AxiosResponse } from 'axios';
import { MfaValidationPageState } from '../../../../interfaces/MfaValidationPageState';
import { getIdentityManagementApiRoot } from '../../../../utils/getIdentityManagementApiRoot';
import { StyledList } from './MfaCodeValidation.styles';
import { MfaMethod } from '../../../../interfaces/MfaMethod';
import { MfaInformationType } from '../../../../interfaces/MfaInformationType';
import MfaActionBar from '../MfaActionBar';
import Constants from '../../../../Constants';
import mapControlErrors from '../../../../utils/mapControlErrors';
import { BFFBadRequest } from '../../../../interfaces/BFFBadRequest';

export interface MfaCodeValidationProps {
  method: MfaMethod;
  informationType: MfaInformationType;
  formatInfo?: (info: string) => string;
}

const MfaCodeValidation: FC<MfaCodeValidationProps> = ({ method, informationType, formatInfo = (info) => info }) => {
  const { t } = useTranslation('MfaValidationPage');
  const stepName = useMemo(() => `${informationType}Code` as const, [informationType]);
  const dispatch = useDispatch();
  const resendingCode = useScopedSelector(({ resources }: MfaValidationPageState) => resources?.sendCode?.pending);
  const [maxAttempts, setMaxAttempts] = useState(false);
  const { [informationType]: information } = useScopedSelector(({ values }: MfaValidationPageState) => values.mfa.informations);

  const handleNewCode = useCallback(() => {
    dispatch(resetFormByName(`forms.${stepName}`));
    dispatch(requestResourceByName('resources.sendCode'));
    setMaxAttempts(false);
  }, [dispatch, stepName]);

  const handleNeedHelp = useCallback(() => {
    dispatch(openModalByName('needHelp'));
  }, [dispatch]);

  const handleSuccess = useCallback(() => {
    dispatch(moveWorkflowForwardByName('workflows.mfa', {}, { replace: true, state: { pageView: true } }));
  }, [dispatch]);

  const transformRequest = useCallback((): { target: string } => ({
    target: information,
  }), [information]);

  const formValidationMessages = useMemo(() => ({
    message: t(`steps.${stepName}.messages.formError`),
    messageMaxAttempts: (
      <Trans
        t={t}
        i18nKey={`steps.${stepName}.messages.formAttemptsError`}
      > a<strong>b</strong>c<Link onClick={handleNewCode}>d</Link>
      </Trans>
    ),
  }), [handleNewCode, stepName, t]);

  const formErrorsSelector = useCallback((response: AxiosResponse<BFFBadRequest>) => {
    const isMaxAttempts = response?.data?.error?.errors[0].code === Constants.MfaMaxAttemptsErrorCode;
    if (isMaxAttempts) {
      setMaxAttempts(true);
    }
    return ({
      message: !isMaxAttempts,
      messageMaxAttempts: isMaxAttempts,
    });
  }, []);

  const controlErrorsSelector = useCallback((response: AxiosResponse<BFFBadRequest>) => {
    const data = response?.data;

    data?.error?.errors?.forEach((error) => {
      if (error.code === Constants.MfaMaxAttemptsErrorCode) {
        error.message = t(`steps.${stepName}.messages.invalidCode`);
      }
    });

    return mapControlErrors(data);
  }, [stepName, t]);

  return (
    <>
      <ResourceAction
        name="resources.sendCode"
        url={`${getIdentityManagementApiRoot()}/${method}/challenge`}
        transformRequest={transformRequest}
      />
      <FormPageTitle data-testid="mfaTitle">{t(`steps.${stepName}.title`)}</FormPageTitle>
      <Block white p-h={0} p-t={0}>
        <InformationsSection
          Icon={DialpadIcon}
          informationsLabel={
            <>
              <Spacing data-testid="mfaInfo" m-b={12}>
                {t(`steps.${stepName}.info`, { information: formatInfo(information), method: t(`steps.${stepName}.methods.${method}.name`) })}
              </Spacing>
              <LinkCollapse
                title={t(`steps.${stepName}.instructions.title`)}
              >
                <StyledList>
                  <li data-testid="mfaInstruction1">{t(`steps.${stepName}.instructions.instruction1`)}</li>
                  <li data-testid="mfaInstruction2">
                    <Link data-testid={`instruction2-${method}-link`} as="button" onClick={handleNewCode}>{t(`steps.${stepName}.instructions.instruction2`)}</Link>
                  </li>
                  <li data-testid="mfaInstruction3">
                    <Link data-testid={`instruction3-${method}-link`} as="button" onClick={handleNeedHelp}>{t(`steps.${stepName}.instructions.instruction3`)}</Link>
                  </li>
                </StyledList>
              </LinkCollapse>
            </>
          }
        />
        <FormSection>
          <CreateForm
            url={`${getIdentityManagementApiRoot()}/validate`}
            name={`forms.${stepName}`}
            valuesDst={`values.${stepName}`}
            dataSrc={`values.${stepName}`}
            dataDst={`data.${stepName}`}
            defaultCol={12}
            messageBottomBorder={36}
            cleanValues
            transformRequest={(v: { code?: string }) => ({ verificationCode: v?.code ?? '' })}
            controlErrorsSelector={controlErrorsSelector}
            validationMessages={formValidationMessages}
            formErrorsSelector={formErrorsSelector}
            onSuccess={handleSuccess}
          >
            <FormRow>
              <FormCol
                label={t(`steps.${stepName}.codeLabel`)}
                data-testid="mfaCode"
              >
                <TextControl
                  name="code"
                  required
                  minLength={Constants.MfaCodeLength}
                  maxLength={Constants.MfaCodeLength}
                  validationMessages={{ code: t(`steps.${stepName}.messages.codeFormat`) }}
                  validators={{ code: (value) => Boolean(value.trim().match(/^[\d]+$/)) }}
                />
              </FormCol>
            </FormRow>
            <MfaActionBar>
              <PrimarySubmitButton disabled={resendingCode || maxAttempts}>
                {t(`steps.${stepName}.confirmButtonText`)}
              </PrimarySubmitButton>
            </MfaActionBar>
            <Spacing m-t={36}>
              <InternalLink href={Constants.MfaInformationsStepSegment}>{t(`steps.${stepName}.cancelButtonText`)}</InternalLink>
            </Spacing>
          </CreateForm>
        </FormSection>
      </Block>
    </>
  );
};

export default MfaCodeValidation;
